import { z } from 'zod';

/**
 * Runtime-validated environment configuration.
 *
 * Only `VITE_`-prefixed variables are available in the browser bundle.
 * We parse `import.meta.env` once at module load so a misconfigured deploy
 * fails fast and loudly instead of producing confusing runtime errors.
 *
 * NEVER read secrets from here — anything in the client env ships to users.
 */
const envSchema = z.object({
  // --- SOAP web-service transport ---
  // Host/prefix the SOAP services live under. Relative (`/soap`, proxied to the
  // WCF host in dev) or absolute (`https://host`) in prod. Per-service paths,
  // namespaces, and SOAPAction shapes live in `lib/soap-services.ts`.
  VITE_SOAP_BASE_URL: z.string().min(1, 'VITE_SOAP_BASE_URL is required'),
  VITE_SOAP_VERSION: z.enum(['1.1', '1.2']).default('1.1'),
  // Per-request transport timeout (ms). Portfolio aggregation operations
  // (summary/holdings/transactions) can take noticeably longer than the light
  // `getPortfolios` call, so the ceiling is generous and tunable per environment.
  VITE_SOAP_TIMEOUT_MS: z.coerce.number().int().positive().default(30_000),
  VITE_APP_NAME: z.string().min(1).default('BFM Online'),
  VITE_APP_ENV: z.enum(['development', 'staging', 'production']).default('development'),
  VITE_ENABLE_QUERY_DEVTOOLS: z
    .enum(['true', 'false'])
    .default('false')
    .transform((value) => value === 'true'),

  // --- Barita OAuth2 authentication server ---
  // BFM Online has no local login — every session starts and ends at this
  // authorization endpoint. It belongs to Barita, not this app, so it is NOT
  // renamed alongside the Conectus → BFM Online migration.
  VITE_OAUTH_AUTHORIZE_URL: z
    .string()
    .min(1)
    .default('http://conectusapp.lan.barita.com/oauth2/authorize'),
  // The client identifier this app presents to the auth server. Must match
  // whatever Barita registers for BFM Online — coordinate before changing.
  VITE_OAUTH_CLIENT_ID: z.string().min(1).default('BFMOnline'),
  VITE_OAUTH_SCOPE: z.string().min(1).default('openid'),
  // `token` = OAuth2 implicit flow, matching the auth server's existing
  // authorization request shape. Change only in lockstep with Barita config.
  VITE_OAUTH_RESPONSE_TYPE: z.string().min(1).default('token'),
  // Exact callback URL registered with Barita for this app. Left blank to
  // derive `${origin}/auth/callback` at runtime — set explicitly only if the
  // registered value must differ from the app's own origin.
  VITE_OAUTH_REDIRECT_URI: z.string().default(''),
  // Expected `iss` claim on the returned access token. Recommended in
  // production; leave blank only while the value is still being confirmed
  // with Barita.
  VITE_OAUTH_ISSUER: z.string().default(''),
  // JWKS endpoint used to cryptographically verify the access token's
  // signature. Without it, tokens are still checked for expiration, issuer,
  // and audience, but signature verification is skipped — set this before
  // going to production.
  VITE_OAUTH_JWKS_URI: z.string().default(''),
});

const parsed = envSchema.safeParse(import.meta.env);

if (!parsed.success) {
  // Surface every missing/invalid var at once, then hard-stop.
  const issues = parsed.error.issues
    .map((issue) => `  - ${issue.path.join('.')}: ${issue.message}`)
    .join('\n');
  throw new Error(`Invalid environment configuration:\n${issues}`);
}

export const env = parsed.data;

export const isProduction = env.VITE_APP_ENV === 'production';
export const isDevelopment = env.VITE_APP_ENV === 'development';
